import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import {Grid, TextField} from "@mui/material";
import {useForm} from "react-hook-form";
import {FormLabel} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {ForeheadVO, useCreateForehead, useQueryForehead, useUpdateForehead} from "@/service/forehead.s";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {checkQueryKey, emptyValue} from "@/helper/method";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";

const validationSchema = z.object({
    id: z.number().nullable().default(0),
    invoiceTitle: z.string({message: "请填发票抬头"}).nonempty({message: "请填发票抬头"}),
    depositBank: z.string({message: "请填写开户行"}).nonempty({message: "请填写开户行"}),
    depositAccount: z.string({message: "请填写开户账户"}).nonempty({message: "请填写开户账户"}),
    depositAddress: z.string({message: "请填写开票地址"}).nonempty({message: "请填写开票地址"}),
    taxNumber: z.string({message: "请填写纳税识别号"}).nonempty({message: "请填写纳税识别号"}),
    mobile: z.string({message: "请填写联系电话"}).nonempty({message: "请填写联系电话"}),
    email: z.string({message: "请填写电子邮箱"}).nonempty({message: "请填写电子邮箱"}),
});

export type WriteForeheadForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WriteForehead = () => {
    const {write, id: drawerId} = useDrawerModal()
    const {data: foreheadData} = useQueryForehead({id: drawerId!}, checkQueryKey(drawerId))
    const {mutateAsync: createForeheadMutate, isPending: createForeheadPending} = useCreateForehead()
    const {mutateAsync: updateForeheadMutate, isPending: updateForeheadPending} = useUpdateForehead()
    const forehead = foreheadData?.data || {} as ForeheadVO
    const {onSearch} = useDrawerModal()
    const {setDrawerModal, setToast} = useGlobalActions()

    const {
        register,
        reset,
        handleSubmit,
        formState: {errors},
    } = useForm<WriteForeheadForm>({
        resolver: zodResolver(validationSchema),
        values: forehead,
        defaultValues: defaultValues,
    });

    const onSubmit = async (values: WriteForeheadForm) => {
        const result = write == 'add' ? await createForeheadMutate(values) : await updateForeheadMutate({...values, id: drawerId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})

        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>发票抬头</FormLabel>
                        <TextField
                            error={!!errors?.invoiceTitle}
                            helperText={errors?.invoiceTitle?.message}
                            {...register("invoiceTitle")}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 40},
                                    endAdornment: <CorporateFareOutlinedIcon color="disabled"/>,
                                },
                            }}
                            placeholder="填写发票抬头"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>纳税识别号</FormLabel>
                        <TextField
                            error={!!errors?.taxNumber}
                            helperText={errors?.taxNumber?.message}
                            {...register("taxNumber")}
                            placeholder="填写纳税识别号"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>开户行</FormLabel>
                        <TextField
                            error={!!errors?.depositBank}
                            helperText={errors?.depositBank?.message}
                            {...register("depositBank")}
                            placeholder="填写开户行"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>开户账户</FormLabel>
                        <TextField
                            error={!!errors?.depositAccount}
                            helperText={errors?.depositAccount?.message}
                            {...register("depositAccount")}
                            placeholder="填写开户账户"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>开户地址</FormLabel>
                        <TextField
                            error={!!errors?.depositAddress}
                            helperText={errors?.depositAddress?.message}
                            {...register("depositAddress")}
                            placeholder="填写开户地址"
                            multiline
                            rows={3}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto'},
                                    inputProps: {maxLength: 250},
                                },
                            }}
                            fullWidth
                        />
                    </FormControl>
                </Grid>


                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>联系电话</FormLabel>
                        <TextField
                            error={!!errors?.mobile}
                            helperText={errors?.mobile?.message}
                            {...register("mobile")}
                            placeholder="填写联系电话"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>电子邮箱</FormLabel>
                        <TextField
                            error={!!errors?.email}
                            helperText={errors?.email?.message}
                            {...register("email")}
                            placeholder="填写电子邮箱"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createForeheadPending || updateForeheadPending} onReset={() => {
                reset(defaultValues)
            }}/>
        </Box>
    );
};
